import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLoader, Button, FullWidthContainer } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { useKnowledgeBase } from './hooks';
import { KnowledgeBasesGrid, KnowledgeBaseFormModal } from './components';

// One knowledge base is the common case: then the overview leads straight to it.
const KnowledgeBasePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { knowledgeBases, initialLoading, error, createKnowledgeBase } = useKnowledgeBase();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const showLoader = useMinimumLoader(initialLoading, { minimumDuration: 500 });

  const handleCreate = async (data: { name: string; description: string }) => {
    const knowledgeBase = await createKnowledgeBase(data);
    if (knowledgeBase) navigate(`/knowledge-base/${knowledgeBase.id}`);
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="text-center py-8">
          <p className="text-red-500 text-lg font-semibold">{t('knowledgeBase.loadFailed')}</p>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
        </div>
      </FullWidthContainer>
    );
  }

  if (knowledgeBases.length === 1) {
    return <Navigate to={`/knowledge-base/${knowledgeBases[0].id}`} replace />;
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('knowledgeBase.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('knowledgeBase.subtitle')}</p>
          </div>
          {knowledgeBases.length > 0 && (
            <Button
              variant="gradient-multi"
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-2"
            >
              <span className="material-icons">add</span>
              {t('knowledgeBase.createKnowledgeBase')}
            </Button>
          )}
        </div>

        {knowledgeBases.length === 0 ? (
          <div className="max-w-xl mx-auto py-16 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                folder
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('knowledgeBase.noKnowledgeBasesFound')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('knowledgeBase.createKnowledgeBaseDescription')}
            </p>
            <Button
              variant="gradient-multi"
              onClick={() => setIsCreateOpen(true)}
              className="inline-flex items-center gap-2"
            >
              <span className="material-icons">add</span>
              {t('knowledgeBase.createKnowledgeBase')}
            </Button>
          </div>
        ) : (
          <KnowledgeBasesGrid
            knowledgeBases={knowledgeBases}
            onOpen={(id, tab) => navigate(`/knowledge-base/${id}${tab ? `/${tab}` : ''}`)}
          />
        )}

        <KnowledgeBaseFormModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSubmit={handleCreate}
          knowledgeBase={null}
        />
      </div>
    </FullWidthContainer>
  );
};

export { KnowledgeBasePage as KnowledgeBase };
